// public/js/modules/charts/sensor-charts.js
// Shared sensor-history chart rendering, used by both the map sensor sidebar
// and the asset detail page. Renders a responsive grid of small line charts
// (one card per metric group) from telemetry history points.
//
// Requires Chart.js (loaded globally via CDN in index.html).

import { convertNumber, displayUnit } from '../../utils/units.js';
import { stampRssiLevels } from '../../utils/rssi-level.js';

// Chart groups, in display order. A card is only rendered if it has data.
// Multiple series can share a card when they use the same unit (e.g. temps).
// Environmental / sensor metrics come first; device-health/technical metrics
// (battery, signal) are flagged `technical:true` and rendered last on their own
// row (see renderSensorChartGrid).
export const METRIC_GROUPS = [
  { title: 'Temperature', unit: '°C', series: [
      { key: 'temperature', label: 'Ambient', color: '#ef4444' },
      { key: 'probe1Temp',  label: 'Probe 1', color: '#f97316' },
      { key: 'probe2Temp',  label: 'Probe 2', color: '#a855f7' },
  ] },
  { title: 'Humidity',       unit: '%',    series: [{ key: 'humidity',       label: 'Humidity', color: '#0ea5e9' }] },
  { title: 'Pressure',       unit: 'hPa',  series: [{ key: 'pressure',       label: 'Pressure', color: '#14b8a6' }] },
  { title: 'Fuel level',     unit: '%',    series: [{ key: 'fuelLevel',      label: 'Fuel',     color: '#eab308' }] },
  // ToF ranger (Engine+): spec range 1..300 cm. `max` caps the "no target"
  // sentinel so an out-of-range reading sits at the 3 m ceiling, not a 30 m spike
  // (also clamps older stored points still in the window). See docs/ble-sensors.md.
  { title: 'Distance (ToF)', unit: 'm',    max: 3, series: [{ key: 'distanceM',      label: 'Distance', color: '#0891b2' }] },
  { title: 'Speed',          unit: 'km/h', series: [{ key: 'speed',          label: 'Speed',    color: '#3b82f6' }] },
  // Shock / impact events (High-G). Sparse by nature, so drawn as individual
  // points rather than a connected line. Appears automatically once the tracker
  // sends High-G data (highG.peakG, or a flat peakG/shock field).
  { title: 'Shock / Impact', unit: 'g',    series: [{
      label: 'Peak G', color: '#dc2626', style: 'points',
      get: (p) => p?.highG?.peakG ?? p?.peakG ?? p?.shock ?? null,
  }] },
  // ── technical / device health (rendered last, on a separate row) ──
  { title: 'Battery',        unit: 'V',    technical: true, series: [{ key: 'battery',        label: 'Voltage', color: '#22c55e' }] },
  { title: 'Battery charge', unit: '%',    technical: true, series: [{ key: 'batteryPercent', label: 'Charge',  color: '#16a34a' }] },
  // Signal: the LEVEL (`_rssiAvg`, stamped by stampRssiLevels - the same trimmed
  // mean the Lambda stores as `rssiAvg`) is what the card is about, because that
  // is the number an engine on the stand moves and a rule can threshold. The raw
  // reads stay on the card as faded context - `role` drives the weight in
  // makeChart - and the level is drawn over them. Listed FIRST so it also wins
  // the stats line and the map event pin. See docs/ble-sensors.md.
  { title: 'Signal (RSSI)',  unit: 'dBm',  technical: true, series: [
      { key: '_rssiAvg',   label: 'Level', color: '#0284c7', role: 'level' },
      // The level charged for its own instability (level - k x spread, same
      // dBm axis), so the gap between the two lines IS the spread. A link that
      // is weak AND erratic sits visibly lower than one that is merely weak.
      { key: '_rssiFloor', label: 'Floor', color: '#0284c7', role: 'floor' },
      // For a BEACON the raw line is `_rssiOwn` - the reads of the OWNER pair
      // only. A tracker on the next stand that overhears the tag reads it 50 dB
      // weaker, and pooling those in drew a sawtooth that looked like signal
      // loss and squashed the band the level actually lives in; they get their
      // own card below (see rssi-level.js). For a TRACKER `rssi` is its own
      // cellular signal - a different quantity on the same axis, with no pair
      // and no owner - so that one passes through unfiltered.
      { label: 'Raw', color: '#64748b', role: 'raw',
        get: (p) => (p?.type === 'bluetooth' ? p._rssiOwn : p?.rssi) },
  ] },
  // Spread gets its own card: dB of width is not dBm of level, and on a
  // -100..-40 axis a 1..8 dB band would be a flat line. It answers a different
  // question too - not how strong the link is but how steady, which is how an
  // intermittent obstruction shows up while the level still looks calm.
  { title: 'Signal spread (RSSI)', unit: 'dB', technical: true, series: [
      { key: '_rssiSpread', label: 'Spread', color: '#c026d3', role: 'level' },
  ] },
  // The reads from trackers that merely overheard the tag. Not noise to be
  // thrown away - "which other gateways can hear this beacon, and how well" is
  // the coverage question, and it is the one thing the main card deliberately
  // cannot answer. Split per gateway (`splitBy`), because one line through
  // several distant trackers is a sawtooth between their levels rather than any
  // one tracker's history. No `role: 'level'`, so it stays behind the Device
  // health toggle where reference detail belongs.
  { title: 'Signal, other gateways', unit: 'dBm', technical: true, splitBy: 'gateway', series: [
      { key: '_rssiFar', label: 'Heard at', color: '#94a3b8' },
  ] },
  { title: 'GSM signal',     unit: '',     technical: true, series: [{ key: 'gsmSignal',      label: 'GSM',     color: '#8b5cf6' }] },
];

export const RANGES = [
  { id: '24h', label: '24 h', hours: 24 },
  { id: '7d',  label: '7 d',  hours: 24 * 7 },
  { id: '14d', label: '14 d', hours: 24 * 14 },
];

// Stable id for a metric group (used to key trail-event state/thresholds).
export function groupIdOf(group) {
  return String(group.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function num(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtTick(ms, hours) {
  const d = new Date(ms);
  return hours <= 24
    ? d.toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(navigator.language, { day: '2-digit', month: '2-digit' });
}

// Full date + time for the point tooltip (so a selected point always shows when
// exactly it was measured, not just the day on multi-day ranges).
function fmtFull(ms) {
  return new Date(ms).toLocaleString(navigator.language, {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function statsLine(values, unit) {
  if (!values.length) return '';
  const cur = values[values.length - 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const r = (n) => Math.round(n * 100) / 100;
  return `<span class="ssr-stat">Now <b>${r(cur)}</b></span>
          <span class="ssr-stat">Min ${r(min)}</span>
          <span class="ssr-stat">Max ${r(max)}</span>
          <span class="ssr-stat">Avg ${r(avg)} ${unit}</span>`;
}

// Short-lived cache of the raw /history response, shared with the map route
// loader (history.js). Both call the exact same endpoint + params, so the map
// can prime this cache and the sensor sidebar reuses it instead of re-fetching.
const RAW_HISTORY_TTL_MS = 90 * 1000;
let _rawHistory = { uuid: null, points: null, ts: 0 };

// What the most recent history fetch through this module actually covered. The
// backend caps a raw read at `coverage.maxPoints` rows and keeps the RECENT end,
// so a long window can come back shortened - and silence about that is exactly
// the bug this exists to prevent: a 40-day request used to arrive as its oldest
// 20 days and get drawn as if it were the whole window.
let _lastCoverage = null;

/** Coverage of the last window fetched through this module, or null. */
export function getLastCoverage() { return _lastCoverage; }

export function setRawHistory(assetUUID, points) {
  _rawHistory = { uuid: assetUUID, points: points || [], ts: Date.now() };
}

export function getRawHistory(assetUUID) {
  if (_rawHistory.uuid === assetUUID && _rawHistory.points &&
      Date.now() - _rawHistory.ts < RAW_HISTORY_TTL_MS) {
    return _rawHistory.points;
  }
  return null;
}

// Fetch the asset's 14-day history and return points sorted ascending with a
// pre-parsed millisecond timestamp (`_t`). Uses ALL points — including the
// heartbeat records without a GPS fix that still carry sensor readings.
// Reuses the raw history cached by the map route loader when still fresh.
export async function fetchHistoryPoints(assetUUID) {
  let raw = getRawHistory(assetUUID);
  if (!raw) {
    const since = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
    // raw=1 → unsimplified points incl. sensor readings without a GPS fix.
    const url = `api/v1/assets/${encodeURIComponent(assetUUID)}/history?raw=1&since=${encodeURIComponent(since)}`;
    const res = await window.authenticatedFetch(url);
    if (!res.ok) throw new Error(`history ${res.status}`);
    const data = await res.json();
    raw = data.history || [];
    _lastCoverage = data.coverage || null;
    setRawHistory(assetUUID, raw);
  }
  // Stamp `_rssiAvg` over the WHOLE fetched window, before any range filter -
  // the level of the first point shown in a 24 h view is built from the samples
  // before it, exactly as the Lambda had them.
  return stampRssiLevels(raw
    .map(p => ({ ...p, _t: new Date(p.timestamp).getTime() }))
    .filter(p => Number.isFinite(p._t))
    .sort((a, b) => a._t - b._t));
}

export function filterByRange(points, rangeId) {
  // A custom (absolute) range is already bounded by the fetch (since/until), so
  // it must NOT be re-windowed relative to now (its window lies in the past).
  if (rangeId === 'custom') return points || [];
  const range = RANGES.find(r => r.id === rangeId) || RANGES[RANGES.length - 1];
  const cutoff = Date.now() - range.hours * 60 * 60 * 1000;
  return (points || []).filter(p => Number.isFinite(p._t) && p._t >= cutoff);
}

// Fetch an ASSET's history for an ABSOLUTE [sinceIso, untilIso] window (data-export
// add-on feature). Same raw=1 endpoint + point shape as fetchHistoryPoints, but an
// explicit range and no shared cache (each custom window is one-off). The backend
// serves it tiered (hot DynamoDB + S3 archive), so it reaches back to activation.
export async function fetchHistoryRange(assetUUID, sinceIso, untilIso) {
  const url = `api/v1/assets/${encodeURIComponent(assetUUID)}/history?raw=1`
    + `&since=${encodeURIComponent(sinceIso)}&until=${encodeURIComponent(untilIso)}`;
  const res = await window.authenticatedFetch(url);
  if (!res.ok) throw new Error(`history ${res.status}`);
  const data = await res.json();
  _lastCoverage = data.coverage || null;
  return stampRssiLevels((data.history || [])
    .map(p => ({ ...p, _t: new Date(p.timestamp).getTime() }))
    .filter(p => Number.isFinite(p._t))
    .sort((a, b) => a._t - b._t));
}

function fmtDuration(ms) {
  const s = ms / 1000;
  if (s < 90) return `${Math.round(s)} s`;
  const m = s / 60;
  if (m < 90) return `${Math.round(m)} min`;
  const h = m / 60;
  if (h < 48) return `${h % 1 ? h.toFixed(1) : h} h`;
  const d = h / 24;
  return `${d % 1 ? d.toFixed(1) : d} d`;
}

// Typical (median) interval between consecutive uplinks within the range —
// median is robust to the odd offline gap. Returns a formatted string or null.
export function uplinkIntervalLabel(points, rangeId) {
  const pts = filterByRange(points, rangeId);
  if (pts.length < 2) return null;
  // Report the median of each device's OWN median cadence — every device counts
  // once, however many rows it emits. This matters on a multi-device asset (a
  // tracker plus the BLE sensors it relays): the tracker can spam a row every few
  // seconds while moving, which — if all timestamps were pooled — would swamp the
  // sensors and report a nonsense "~7 s" when the sensors actually report every
  // ~30 min. Weighting per device makes the hyperactive tracker just one vote, so
  // the label reflects the typical device's cadence. Single-device assets are
  // unaffected (one device = its own median).
  const byDevice = new Map();
  for (const p of pts) {
    const k = p.deviceId || '';
    if (!byDevice.has(k)) byDevice.set(k, []);
    byDevice.get(k).push(p._t);
  }
  const median = (arr) => arr[Math.floor(arr.length / 2)];
  const deviceMedians = [];
  for (const times of byDevice.values()) {
    times.sort((a, b) => a - b);
    const ds = [];
    for (let i = 1; i < times.length; i++) {
      const d = times[i] - times[i - 1];
      if (d > 0) ds.push(d);
    }
    if (ds.length) { ds.sort((a, b) => a - b); deviceMedians.push(median(ds)); }
  }
  if (!deviceMedians.length) return null;
  deviceMedians.sort((a, b) => a - b);
  return fmtDuration(median(deviceMedians));
}

// `detail` = the fullscreen-modal variant: larger fonts, more ticks, and visible
// sample points on line series (there is room to show every reading).
const DAY_MS = 86400000;

// Local midnight at or before `ms`. Weekends and day boundaries are drawn in the
// viewer's LOCAL time because that is the calendar the person reading the chart
// works in - a UTC midnight line would sit an hour or two off every label.
function localMidnight(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// Calendar context behind the data: a tint on Saturdays and Sundays and a line
// at every local midnight. Drawn in `beforeDatasetsDraw` so it stays strictly
// behind every series. Both parts switch themselves off when they would stop
// being readable - a 120-day window has no room for 120 day lines, and a
// sub-2-day window would be one flat tint rather than a weekend marker.
const timeBandsPlugin = {
  id: 'timeBands',
  beforeDatasetsDraw(chart) {
    const area = chart.chartArea;
    const x = chart.scales?.x;
    if (!area || !x || !Number.isFinite(x.min) || !Number.isFinite(x.max)) return;
    const span = x.max - x.min;
    if (span <= 0) return;
    const dayPx = (area.right - area.left) * DAY_MS / span;
    const bands = span >= 2 * DAY_MS;      // below that, a tint says nothing
    const lines = dayPx >= 8;              // below that, lines become a grid
    if (!bands && !lines) return;

    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(area.left, area.top, area.right - area.left, area.bottom - area.top);
    ctx.clip();
    // Walk real calendar days (setDate, not +86.4e6), so a DST change does not
    // drift every later line an hour off the day it belongs to.
    for (let day = new Date(localMidnight(x.min)); day.getTime() <= x.max; day.setDate(day.getDate() + 1)) {
      const start = day.getTime();
      const next = new Date(day); next.setDate(next.getDate() + 1);
      const dow = day.getDay();
      if (bands && (dow === 0 || dow === 6)) {
        const x0 = Math.max(area.left, x.getPixelForValue(start));
        const x1 = Math.min(area.right, x.getPixelForValue(next.getTime()));
        if (x1 > x0) {
          ctx.fillStyle = 'rgba(37, 99, 235, 0.055)';
          ctx.fillRect(x0, area.top, x1 - x0, area.bottom - area.top);
        }
      }
      if (lines && start >= x.min) {
        const px = Math.round(x.getPixelForValue(start)) + 0.5;
        ctx.beginPath();
        ctx.moveTo(px, area.top);
        ctx.lineTo(px, area.bottom);
        // Monday a touch stronger, so a week reads as one block at a glance.
        ctx.strokeStyle = dow === 1 ? 'rgba(71, 85, 105, 0.35)' : 'rgba(100, 116, 139, 0.18)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    ctx.restore();
  },
};

// A horizontal line that follows the cursor, with the value it sits at printed
// on the axis edge. The tooltip already answers "which reading is this"; this
// answers "what level is my cursor at" - reading a signal level against a rule
// threshold, or a temperature against a limit, without eyeballing the axis.
// Built as a factory because the readout needs the card's unit.
function cursorLinePlugin(group, detail) {
  const unit = displayUnit(group.unit);
  return {
    id: 'cursorLine',
    afterEvent(chart, args) {
      const e = args.event;
      const a = chart.chartArea;
      const inside = e.x >= a.left && e.x <= a.right && e.y >= a.top && e.y <= a.bottom;
      const next = (inside && e.type !== 'mouseout') ? e.y : null;
      if (next !== chart.$cursorY) {
        chart.$cursorY = next;
        args.changed = true;      // ask Chart.js for a repaint
      }
    },
    afterDatasetsDraw(chart) {
      const y = chart.$cursorY;
      const a = chart.chartArea;
      if (y == null || !a) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([4, 3]);
      ctx.moveTo(a.left, y + 0.5);
      ctx.lineTo(a.right, y + 0.5);
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.45)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
      // A 0/1 state card has no meaningful value between its two states, so it
      // gets the alignment line without a nonsense readout like "0.37".
      if (!group.stepped) {
        const v = chart.scales?.y?.getValueForPixel(y);
        if (Number.isFinite(v)) {
          const txt = `${Math.round(v * 10) / 10}${unit ? ' ' + unit : ''}`;
          const fs = detail ? 12 : 10;
          ctx.font = `${fs}px system-ui, sans-serif`;
          const w = ctx.measureText(txt).width + 8;
          const h = fs + 6;
          const bx = a.left + 2;
          const by = Math.min(Math.max(y - h / 2, a.top), a.bottom - h);
          ctx.fillStyle = 'rgba(15, 23, 42, 0.78)';
          ctx.beginPath();
          ctx.roundRect ? ctx.roundRect(bx, by, w, h, 3) : ctx.rect(bx, by, w, h);
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.textBaseline = 'middle';
          ctx.fillText(txt, bx + 4, by + h / 2);
        }
      }
      ctx.restore();
    },
  };
}

function makeChart(ctx, group, datasets, range, detail = false) {
  const multi = datasets.length > 1;
  const fs = detail ? 13 : 9;         // axis-tick font size
  return new Chart(ctx, {
    type: 'line',
    // Local to this chart on purpose: registering them globally would also
    // decorate the battery-remaining projection charts, which are not a
    // calendar view of readings.
    plugins: [timeBandsPlugin, cursorLinePlugin(group, detail)],
    data: {
      datasets: datasets.map(ds => {
        const asPoints = ds.meta.style === 'points';
        const stepped = !!group.stepped;   // derived 0/1 state → square step line
        // `role` ranks series within one card. 'raw' is context: hairline,
        // translucent, no points, no fill, drawn UNDER everything. 'floor' is a
        // derived bound on the level: same weight as raw but dashed, so it
        // cannot be mistaken for a measurement. 'level' is the headline:
        // thicker than a normal series and drawn last.
        // Chart.js sorts datasets by `order` and then draws that list in
        // REVERSE, so the LOWEST order ends up on top.
        const raw = ds.meta.role === 'raw';
        const level = ds.meta.role === 'level';
        const floor = ds.meta.role === 'floor';
        const fade = (c) => (/^#[0-9a-f]{6}$/i.test(c) ? c + '59' : c);   // ~35%
        return {
          label: ds.meta.label,
          data: ds.data,
          borderColor: (raw || floor) ? fade(ds.meta.color) : ds.meta.color,
          backgroundColor: asPoints ? ds.meta.color : ds.meta.color + '22',
          borderWidth: (raw || floor) ? (detail ? 1.5 : 1) : (level ? (detail ? 3 : 2.5) : (detail ? 2.5 : 2)),
          // Dashed, so the floor reads as a derived bound rather than a second
          // measurement sitting next to the level.
          ...(floor ? { borderDash: [5, 3] } : {}),
          order: level ? 0 : (raw ? 2 : 1),
          showLine: !asPoints,
          pointRadius: asPoints ? (detail ? 5 : 4) : ((raw || floor || !detail) ? 0 : 2),
          pointHoverRadius: asPoints ? 6 : (detail ? 6 : 4),
          tension: stepped ? 0 : 0.25,
          stepped: stepped ? 'after' : false,
          fill: !multi && !asPoints,
          // A floor line must never fill: the band it would paint reaches down
          // to the axis, not to the level, and reads as data that is not there.
          ...(floor ? { fill: false } : {}),
          spanGaps: true,
        };
      }),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: multi, position: 'bottom', labels: { boxWidth: 10, font: { size: detail ? 13 : 10 } } },
        tooltip: {
          callbacks: {
            title: (items) => fmtFull(items[0].parsed.x),
            label: (item) => {
              if (group.stepped && group.stateLabels) return `${item.dataset.label}: ${group.stateLabels[item.parsed.y] ?? item.parsed.y}`;
              const u = displayUnit(group.unit); return `${item.dataset.label}: ${Math.round(item.parsed.y * 100) / 100}${u ? ' ' + u : ''}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          ticks: { maxTicksLimit: detail ? 12 : 5, font: { size: fs }, callback: (v) => fmtTick(v, range.hours) },
          grid: { display: false },
        },
        y: (group.stepped ? {
          // Derived 0/1 state: pin the axis to the two states and label them.
          min: 0, max: 1, offset: true,
          ticks: { stepSize: 1, font: { size: fs }, callback: (v) => (group.stateLabels ? (group.stateLabels[v] ?? '') : v) },
          grid: { color: 'rgba(0,0,0,0.05)' },
        } : {
          ticks: { maxTicksLimit: detail ? 8 : 5, font: { size: fs } },
          grid: { color: 'rgba(0,0,0,0.05)' },
        }),
      },
    },
  });
}

// Per-series statistics for the fullscreen detail table. Numeric series get
// Now/Min/Max/Avg; derived 0/1 series get the current state + % of time active.
function statsTableHtml(group, datasets) {
  const unit = displayUnit(group.unit);
  const r = (n) => Math.round(n * 100) / 100;
  const stepped = !!group.stepped;
  const head = stepped
    ? `<tr><th>Series</th><th class="text-end">Samples</th><th>State now</th><th class="text-end">% ${group.stateLabels?.[1] ?? 'active'}</th></tr>`
    : `<tr><th>Series</th><th class="text-end">Samples</th><th class="text-end">Now</th><th class="text-end">Min</th><th class="text-end">Max</th><th class="text-end">Avg</th></tr>`;
  const rows = datasets.map(ds => {
    const ys = ds.data.map(d => d.y).filter(v => v != null);
    if (!ys.length) return '';
    if (stepped) {
      const on = ys.filter(v => v === 1).length;
      const pct = Math.round((on / ys.length) * 100);
      const cur = group.stateLabels?.[ys[ys.length - 1]] ?? ys[ys.length - 1];
      return `<tr><td>${ds.meta.label}</td><td class="text-end">${ds.data.length}</td><td>${cur}</td><td class="text-end">${pct}%</td></tr>`;
    }
    const cur = ys[ys.length - 1];
    const avg = ys.reduce((a, b) => a + b, 0) / ys.length;
    const u = unit ? ` ${unit}` : '';
    return `<tr><td>${ds.meta.label}</td><td class="text-end">${ds.data.length}</td>`
      + `<td class="text-end"><b>${r(cur)}</b>${u}</td><td class="text-end">${r(Math.min(...ys))}</td>`
      + `<td class="text-end">${r(Math.max(...ys))}</td><td class="text-end">${r(avg)}</td></tr>`;
  }).join('');
  return `<thead>${head}</thead><tbody>${rows}</tbody>`;
}

// Open one chart in an in-app fullscreen modal (Bootstrap modal-fullscreen — NOT
// the browser Fullscreen API), re-rendered larger with a per-series stats table.
function openChartModal(u, range) {
  if (typeof bootstrap === 'undefined') return;
  const { group, datasets } = u;
  const unit = displayUnit(group.unit);
  const allX = datasets.flatMap(d => d.data.map(p => p.x));
  const total = datasets.reduce((s, d) => s + d.data.length, 0);
  const spanTxt = allX.length
    ? `${fmtFull(Math.min(...allX))} — ${fmtFull(Math.max(...allX))} · span ${fmtDuration(Math.max(...allX) - Math.min(...allX))} · ${total} samples`
    : '';

  const el = document.createElement('div');
  el.className = 'modal fade';
  el.tabIndex = -1;
  el.innerHTML = `
    <div class="modal-dialog modal-fullscreen">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">${group.title}${unit ? ` <span class="text-muted fw-normal">(${unit})</span>` : ''}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body d-flex flex-column" style="gap:1rem;overflow:hidden;">
          <div class="text-muted small">${spanTxt}</div>
          <div style="flex:1 1 auto;min-height:0;position:relative;"><canvas></canvas></div>
          <div class="table-responsive" style="flex:0 0 auto;max-height:35vh;overflow:auto;">
            <table class="table table-sm mb-0 align-middle" style="max-width:760px;">${statsTableHtml(group, datasets)}</table>
          </div>
        </div>
      </div>
    </div>`;
  document.body.appendChild(el);
  const modal = new bootstrap.Modal(el);
  let chart = null;
  // Build the chart only once the modal has its final size, so Chart.js measures
  // the full canvas rather than the 0×0 of a hidden element.
  el.addEventListener('shown.bs.modal', () => {
    const ctx = el.querySelector('canvas');
    if (ctx && typeof Chart !== 'undefined') chart = makeChart(ctx, group, datasets, range, true);
  });
  el.addEventListener('hidden.bs.modal', () => {
    try { chart?.destroy(); } catch (e) {}
    el.remove();
  });
  modal.show();
}

/**
 * Render a responsive grid of sensor charts into `container`.
 * @param {HTMLElement} container  target element (innerHTML is replaced)
 * @param {object[]} points        history points with a pre-parsed `_t` (ms)
 * @param {string} rangeId         one of RANGES ids
 * @returns {Chart[]} created Chart instances (caller is responsible for destroy)
 */
export function renderSensorChartGrid(container, points, rangeId, deviceLabels = null, opts = {}) {
  if (!container) return [];

  if (typeof Chart === 'undefined') {
    container.innerHTML = `<div class="text-muted small p-3">Chart library not loaded.</div>`;
    return [];
  }

  const pts = filterByRange(points, rangeId);
  if (!pts.length) {
    container.innerHTML = `<div class="text-muted small p-3">No history data for this range.</div>`;
    return [];
  }

  // Preset ranges use their fixed window for tick granularity; a custom (absolute)
  // range derives it from the actual span of the returned points.
  const range = rangeId === 'custom'
    ? { id: 'custom', hours: Math.max(1, (pts[pts.length - 1]._t - pts[0]._t) / 3600000) }
    : (RANGES.find(r => r.id === rangeId) || RANGES[RANGES.length - 1]);

  // Distinct source devices in range, in first-seen order. When several devices
  // on one asset report the same metric (e.g. tracker + ELA both send temp), we
  // draw one line PER device instead of conflating them into a single series.
  const deviceOrder = [];
  for (const p of pts) {
    const d = p.deviceId || '';
    if (!deviceOrder.includes(d)) deviceOrder.push(d);
  }
  // A group may ask to be split one line per (device, GATEWAY) pair instead -
  // for the "other gateways" card, where the gateway IS the thing that
  // distinguishes two lines and the device is the same beacon throughout.
  const pairKeyOf = (p) => `${p.deviceId || ''}\u0000${p.gatewayDeviceId || ''}`;
  const pairOrder = [];
  for (const p of pts) {
    const k = pairKeyOf(p);
    if (!pairOrder.includes(k)) pairOrder.push(k);
  }
  const splitOf = (group) => (group.splitBy === 'gateway'
    ? { order: pairOrder, keyOf: pairKeyOf }
    : { order: deviceOrder, keyOf: (p) => p.deviceId || '' });

  const DEVICE_PALETTE = ['#3b82f6', '#ef4444', '#22c55e', '#a855f7', '#f97316', '#0891b2', '#eab308'];
  const labelForDevice = (d) => {
    // Prefer the slot display label ("Engine Bag") supplied by the caller, so a
    // renamed sub-asset's series carries its name and identical sensors differ.
    if (deviceLabels && deviceLabels[d]) return deviceLabels[d];
    const p = pts.find(pp => (pp.deviceId || '') === d);
    const src = (p && (p.source || p.subType || p.type)) || '';
    const short = d ? String(d).replace(/^BLE_/, '').slice(-4) : '';
    return [src, short].filter(Boolean).join(' ·') || 'device';
  };

  // Label for one split key. A gateway-split series is named by its GATEWAY -
  // that is what separates it from its siblings - and keeps the beacon's name
  // only when more than one beacon is on the card.
  const labelForKey = (group, key, devices) => {
    if (group.splitBy !== 'gateway') return labelForDevice(key);
    const [dev, gw] = String(key).split('\u0000');
    const gwLabel = gw ? `GW ${String(gw).slice(-4)}` : 'no gateway';
    return devices > 1 ? `${labelForDevice(dev)} · ${gwLabel}` : gwLabel;
  };

  // Derived-sensor groups: a derived value rides on the point as a 0/1 number `k`
  // plus a companion `${k}Label`. Detect those keys from the data and render each
  // as its own stepped 0/1 chart (labels on the axis), so no static catalog entry
  // is needed. See docs/derived-sensors.md.
  const humanize = (k) => k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
  const derivedGroups = [];
  const derivedSeen = new Set();
  for (const p of pts) {
    for (const key of Object.keys(p)) {
      if (derivedSeen.has(key) || !key.endsWith('Label')) continue;
      const base = key.slice(0, -'Label'.length);
      if (!base || typeof p[base] !== 'number') continue;
      derivedSeen.add(base); derivedSeen.add(key);
      // Resolve the two state labels from whatever points carry them.
      const on = pts.find(q => q[base] === 1 && q[key])?.[key] || 'active';
      const off = pts.find(q => q[base] === 0 && q[base + 'Label'])?.[base + 'Label'] || 'inactive';
      derivedGroups.push({
        title: humanize(base), unit: '', derived: true, stepped: true,
        stateLabels: { 0: off, 1: on },
        series: [{ key: base, label: humanize(base), color: '#7c3aed' }],
      });
    }
  }

  const usable = [...METRIC_GROUPS, ...derivedGroups].map(group => {
    const datasets = [];
    const { order, keyOf } = splitOf(group);
    group.series.forEach(s => {
      const valOf = (p) => (s.get ? s.get(p) : p[s.key]);
      // Which devices (or device/gateway pairs) actually report this metric in range?
      const perDevice = order.map(d => {
        const data = pts
          .filter(p => keyOf(p) === d)
          .map(p => {
            let y = num(valOf(p));
            // Cap sentinel/out-of-range values at the group ceiling (ToF distance).
            if (y != null && group.max != null) y = Math.min(y, group.max);
            return { x: p._t, y: y == null ? null : convertNumber(y, group.unit) };
          })
          .filter(pt => pt.y != null);
        return data.length ? { device: d, data } : null;
      }).filter(Boolean);
      if (!perDevice.length) return;

      const multi = perDevice.length > 1;
      // A gateway-split card is ALWAYS named per series, even with one line:
      // "Heard at" alone would not say which tracker did the hearing.
      const named = multi || group.splitBy === 'gateway';
      const beacons = new Set(perDevice.map(pd => String(pd.device).split('\u0000')[0])).size;
      perDevice.forEach(pd => {
        const color = multi
          ? DEVICE_PALETTE[order.indexOf(pd.device) % DEVICE_PALETTE.length]
          : s.color;
        const label = named ? `${s.label} · ${labelForKey(group, pd.device, beacons)}` : s.label;
        datasets.push({ meta: { ...s, color, label }, data: pd.data });
      });
    });
    return datasets.length ? { group, datasets } : null;
  }).filter(Boolean);

  if (!usable.length) {
    container.innerHTML = `<div class="text-muted small p-3">No numeric sensor values recorded in this range.</div>`;
    return [];
  }

  // Environmental/sensor charts first; technical (battery/signal) on a separate
  // row below a subtle divider. Indices stay global so canvas ids are unique.
  // Optional per-metric "Show on map" event controls (map sensor sidebar only):
  // a switch + from/to threshold inputs in the user's display unit. Derived 0/1
  // metrics are excluded — a numeric threshold makes no sense there.
  const ev = opts.eventControls || null;
  const eventRowHtml = (group) => {
    if (!ev || group.derived) return '';
    const gid = groupIdOf(group);
    const enabled = ev.isEnabled(gid);
    const t = ev.getThresholds(gid);
    const unit = displayUnit(group.unit);
    const val = (v) => (v == null ? '' : `value="${v}"`);
    return `
      <div class="ssr-event-controls" data-gid="${gid}">
        <label class="ssr-event-switch form-check form-switch mb-0">
          <input type="checkbox" class="form-check-input js-ssr-ev-switch" ${enabled ? 'checked' : ''}>
          <span>Show on map</span>
        </label>
        <span class="ssr-event-fields" ${enabled ? '' : 'hidden'}>
          <input type="number" step="any" class="form-control form-control-sm js-ssr-ev-from" placeholder="from" ${val(t.from)}>
          <span>&ndash;</span>
          <input type="number" step="any" class="form-control form-control-sm js-ssr-ev-to" placeholder="to" ${val(t.to)}>
          ${unit ? `<span class="text-muted">${unit}</span>` : ''}
        </span>
      </div>`;
  };

  const cardHtml = (u, i) => {
    const u_unit = displayUnit(u.group.unit);
    // One series → its stats. Several, but exactly one of them the LEVEL (the
    // signal card: level + its faded raw) → the level's, since that is the
    // number being read. Several devices → none, as before.
    const levelSets = u.datasets.filter(d => d.meta.role === 'level');
    const headline = u.datasets.length === 1 ? u.datasets[0]
      : (levelSets.length === 1 ? levelSets[0] : null);
    const stats = headline ? statsLine(headline.data.map(d => d.y), u_unit) : '';
    return `
      <div class="sensor-card">
        <div class="ssr-card-head">
          <span class="ssr-card-title">${u.group.title}${u_unit ? ` <span class="text-muted">(${u_unit})</span>` : ''}</span>
          <span class="ssr-card-stats">${stats}</span>
          <button type="button" class="ssr-expand" data-idx="${i}" title="Expand chart" aria-label="Expand chart"><i class="fa-solid fa-up-right-and-down-left-from-center"></i></button>
        </div>
        ${eventRowHtml(u.group)}
        <div class="ssr-card-canvas"><canvas id="ssr-canvas-${i}"></canvas></div>
      </div>`;
  };

  const envItems = [];
  const techItems = [];
  // Signal is device health for a tracker (its cellular RSSI) but NOT for an
  // asset whose BLE pair level tells you whether an engine is sitting on the
  // stand - that one belongs in the main grid, not behind a collapsed toggle.
  usable.forEach((u, i) => {
    const hasLevel = u.datasets.some(d => d.meta.role === 'level');
    ((u.group.technical && !hasLevel) ? techItems : envItems).push({ u, i });
  });

  let html = '';
  if (envItems.length) html += `<div class="sensor-chart-grid">${envItems.map(({ u, i }) => cardHtml(u, i)).join('')}</div>`;
  if (techItems.length) {
    // Device-health charts (battery, signal) are collapsed by default — they are
    // reference detail, not the day-to-day metrics. Chart.js (ResizeObserver) fits
    // them to the container once the collapse opens.
    html += `<button type="button" class="ssr-health-toggle collapsed text-muted small mt-3 mb-1 d-flex align-items-center gap-2 w-100"
        data-bs-toggle="collapse" data-bs-target="#ssr-health-collapse" aria-expanded="false" aria-controls="ssr-health-collapse">
        <i class="fa-solid fa-chevron-right ssr-health-chevron"></i> Device health <span class="text-muted">(${techItems.length})</span>
      </button>`;
    html += `<div class="collapse" id="ssr-health-collapse"><div class="sensor-chart-grid">${techItems.map(({ u, i }) => cardHtml(u, i)).join('')}</div></div>`;
  }
  container.innerHTML = html;

  const charts = [];
  usable.forEach((u, i) => {
    const ctx = container.querySelector(`#ssr-canvas-${i}`);
    if (ctx) charts.push(makeChart(ctx, u.group, u.datasets, range));
  });

  // Per-chart expand → in-app fullscreen modal (delegated so it survives redraws).
  container.querySelectorAll('.ssr-expand').forEach(btn => {
    btn.addEventListener('click', () => {
      const u = usable[Number(btn.dataset.idx)];
      if (u) openChartModal(u, range);
    });
  });

  // Wire the event controls (switch + thresholds) back to the caller.
  if (ev) {
    const groupByGid = new Map(usable.map(u => [groupIdOf(u.group), u.group]));
    container.querySelectorAll('.ssr-event-controls').forEach(row => {
      const group = groupByGid.get(row.dataset.gid);
      if (!group) return;
      const sw = row.querySelector('.js-ssr-ev-switch');
      const fields = row.querySelector('.ssr-event-fields');
      const fromIn = row.querySelector('.js-ssr-ev-from');
      const toIn = row.querySelector('.js-ssr-ev-to');
      const readThresholds = () => ({
        from: fromIn.value === '' ? NaN : Number(fromIn.value),
        to: toIn.value === '' ? NaN : Number(toIn.value),
      });
      sw?.addEventListener('change', () => {
        if (fields) fields.hidden = !sw.checked;
        ev.onToggle(group, sw.checked);
      });
      const onThreshold = () => ev.onThreshold(group, readThresholds());
      fromIn?.addEventListener('change', onThreshold);
      toIn?.addEventListener('change', onThreshold);
    });
  }
  return charts;
}
